import { API_BASE_URL } from '../config'
import { getAccessToken } from './auth'

/**
 * Submit a PDF export job. Deducts one credit server-side immediately.
 * Returns job_id for polling — does NOT wait for the PDF to be rendered.
 *
 * @param {Object} payload  - Same shape as the old exportPDF payload
 * @returns {Promise<{ success, jobId, position, creditsRemaining, error, noCredits }>}
 */
export async function submitPdfJob(payload) {
  const token = getAccessToken()
  if (!token) {
    return { success: false, error: 'Sign in to export PDF reports.' }
  }

  try {
    const res = await fetch(`${API_BASE_URL}/api/v1/export/pdf`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    })

    if (!res.ok) {
      const errData = await res.json().catch(() => null)
      const detail = errData?.detail || `Export failed (${res.status})`
      return {
        success: false,
        error: detail,
        noCredits: res.status === 403 && detail.includes('No PDF credits'),
      }
    }

    const data = await res.json()
    return {
      success: true,
      jobId: data.job_id,
      position: data.position,
      creditsRemaining: data.credits_remaining,
    }
  } catch (err) {
    console.error('PDF submit error:', err)
    return { success: false, error: 'Network error. Please try again.' }
  }
}

/**
 * Poll the status of a pending PDF job.
 *
 * @param {string} jobId
 * @returns {Promise<{ job_id, status, position, error }>}
 *   status: "queued" | "rendering" | "completed" | "failed"
 */
export async function pollPdfStatus(jobId) {
  const token = getAccessToken()
  if (!token) return { status: 'failed', error: 'Not authenticated.' }

  try {
    const res = await fetch(`${API_BASE_URL}/api/v1/export/pdf/status/${jobId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) return { status: 'failed', error: `Status check failed (${res.status})` }
    return await res.json()
  } catch {
    return { status: 'failed', error: 'Network error.' }
  }
}

/**
 * Download a completed PDF job. Triggers a browser download automatically.
 *
 * @param {string} jobId
 * @returns {Promise<{ success, error? }>}
 */
export async function downloadPdf(jobId) {
  const token = getAccessToken()
  if (!token) return { success: false, error: 'Not authenticated.' }

  try {
    const res = await fetch(`${API_BASE_URL}/api/v1/export/pdf/download/${jobId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) {
      const errData = await res.json().catch(() => null)
      return { success: false, error: errData?.detail || 'Download failed.' }
    }

    const blob = await res.blob()
    const disposition = res.headers.get('Content-Disposition') || ''
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/)
    const filename = filenameMatch ? filenameMatch[1] : 'SideStreet_Report.pdf'
    _triggerDownload(blob, filename)
    return { success: true }
  } catch (err) {
    console.error('PDF download error:', err)
    return { success: false, error: 'Network error. Please try again.' }
  }
}

/**
 * Delete a PDF job the user owns.
 *
 * @param {string} jobId
 * @returns {Promise<{ success, error? }>}
 */
export async function deletePdfJob(jobId) {
  const token = getAccessToken()
  if (!token) return { success: false, error: 'Not authenticated.' }

  try {
    const res = await fetch(`${API_BASE_URL}/api/v1/export/pdf/${jobId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) {
      const errData = await res.json().catch(() => null)
      return { success: false, error: errData?.detail || 'Delete failed.' }
    }
    return { success: true }
  } catch (err) {
    console.error('PDF delete error:', err)
    return { success: false, error: 'Network error. Please try again.' }
  }
}

/**
 * List all PDF jobs for the current user, with storage usage stats.
 *
 * @returns {Promise<{ jobs, storage_used, storage_limit, tier, error? }>}
 */
export async function listPdfJobs() {
  const token = getAccessToken()
  if (!token) return { jobs: [], error: 'Not authenticated.' }

  try {
    const res = await fetch(`${API_BASE_URL}/api/v1/export/pdf/list`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) {
      const errData = await res.json().catch(() => null)
      return { jobs: [], error: errData?.detail || 'Failed to load reports.' }
    }
    return await res.json()
  } catch (err) {
    console.error('PDF list error:', err)
    return { jobs: [], error: 'Network error.' }
  }
}

function _triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  // Append today's date so Android Chrome never confuses this with a previously
  // downloaded file of the same base name sitting in the Downloads folder.
  const date = new Date().toISOString().slice(0, 10)
  const uniqueFilename = filename.endsWith('.pdf')
    ? filename.slice(0, -4) + `_${date}.pdf`
    : `${filename}_${date}`
  a.download = uniqueFilename
  document.body.appendChild(a)
  a.click()
  // Chrome on Android needs time to read the blob before the URL is revoked.
  // 100ms is too short on mobile — use 60s so the download can fully initiate.
  setTimeout(() => {
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, 60000)
}
